import { useState } from "react";
import { Link } from "react-router-dom";
import ServiceStatusWidget from "./ServiceStatusWidget";
import GoogleCalendarWidget from "./GoogleCalendarWidget";

const PORTAL_URL = "https://sdydqpjhqwlzaedmsncd.supabase.co/functions/v1/stripe-portal";

export interface SubData {
  email: string;
  plan_id: string;
  status: string;
  billing: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
  setup_completed: boolean;
  assigned_phone: string | null;
  preferred_lang: string | null;
  has_stripe: boolean;
}

interface PlanConfig {
  name: string;
  nameFull: string;
  minutes: string;
  color: string;
  bgColor: string;
  borderColor: string;
  features: { icon: string; text: string }[];
}

const PLAN_CONFIGS_ES: Record<string, PlanConfig> = {
  basico: {
    name: "Básico",
    nameFull: "Plan Básico",
    minutes: "500 min/mes",
    color: "text-stone-700",
    bgColor: "bg-stone-100",
    borderColor: "border-stone-200",
    features: [
      { icon: "ri-phone-line", text: "Recepcionista IA activo 24/7/365" },
      { icon: "ri-global-line", text: "Disponible en 31 idiomas" },
      { icon: "ri-phone-forward-line", text: "Desvío de llamadas configurado" },
      { icon: "ri-file-text-line", text: "Canvas Memory básico" },
      { icon: "ri-headphone-line", text: "500 minutos de llamadas al mes" },
      { icon: "ri-mail-line", text: "Soporte por email" },
    ],
  },
  profesional: {
    name: "Profesional",
    nameFull: "Plan Profesional",
    minutes: "1.500 min/mes",
    color: "text-amber-700",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-100",
    features: [
      { icon: "ri-phone-line", text: "Recepcionista IA activo 24/7/365" },
      { icon: "ri-global-line", text: "Disponible en 31 idiomas" },
      { icon: "ri-phone-forward-line", text: "Desvío de llamadas configurado" },
      { icon: "ri-file-text-line", text: "Canvas Memory personalizado" },
      { icon: "ri-calendar-2-line", text: "Gestión de citas con Google Calendar" },
      { icon: "ri-headphone-line", text: "1.500 minutos de llamadas al mes" },
      { icon: "ri-customer-service-2-line", text: "Soporte prioritario" },
    ],
  },
  premium: {
    name: "Premium",
    nameFull: "Plan Premium",
    minutes: "Ilimitados",
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    features: [
      { icon: "ri-phone-line", text: "Recepcionista IA activo 24/7/365" },
      { icon: "ri-global-line", text: "Disponible en 31 idiomas" },
      { icon: "ri-phone-forward-line", text: "Desvío de llamadas configurado" },
      { icon: "ri-file-text-line", text: "Canvas Memory avanzado con IA" },
      { icon: "ri-calendar-2-line", text: "Gestión de citas con Google Calendar" },
      { icon: "ri-infinity-line", text: "Minutos ilimitados de llamadas" },
      { icon: "ri-database-2-line", text: "Integración CRM con FastMCP" },
      { icon: "ri-fingerprint-line", text: "Branded Call ID personalizado" },
      { icon: "ri-vip-crown-2-line", text: "Account manager dedicado" },
      { icon: "ri-shield-check-line", text: "SLA de disponibilidad 99,9%" },
    ],
  },
};

const PLAN_CONFIGS_EN: Record<string, PlanConfig> = {
  basico: {
    name: "Basic",
    nameFull: "Basic Plan",
    minutes: "500 min/mo",
    color: "text-stone-700",
    bgColor: "bg-stone-100",
    borderColor: "border-stone-200",
    features: [
      { icon: "ri-phone-line", text: "AI Receptionist active 24/7/365" },
      { icon: "ri-global-line", text: "Available in 31 languages" },
      { icon: "ri-phone-forward-line", text: "Call forwarding configured" },
      { icon: "ri-file-text-line", text: "Basic Canvas Memory" },
      { icon: "ri-headphone-line", text: "500 call minutes per month" },
      { icon: "ri-mail-line", text: "Email support" },
    ],
  },
  profesional: {
    name: "Professional",
    nameFull: "Professional Plan",
    minutes: "1,500 min/mo",
    color: "text-amber-700",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-100",
    features: [
      { icon: "ri-phone-line", text: "AI Receptionist active 24/7/365" },
      { icon: "ri-global-line", text: "Available in 31 languages" },
      { icon: "ri-phone-forward-line", text: "Call forwarding configured" },
      { icon: "ri-file-text-line", text: "Custom Canvas Memory" },
      { icon: "ri-calendar-2-line", text: "Appointment management via Google Calendar" },
      { icon: "ri-headphone-line", text: "1,500 call minutes per month" },
      { icon: "ri-customer-service-2-line", text: "Priority support" },
    ],
  },
  premium: {
    name: "Premium",
    nameFull: "Premium Plan",
    minutes: "Unlimited",
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    features: [
      { icon: "ri-phone-line", text: "AI Receptionist active 24/7/365" },
      { icon: "ri-global-line", text: "Available in 31 languages" },
      { icon: "ri-phone-forward-line", text: "Call forwarding configured" },
      { icon: "ri-file-text-line", text: "Advanced AI Canvas Memory" },
      { icon: "ri-calendar-2-line", text: "Appointment management via Google Calendar" },
      { icon: "ri-infinity-line", text: "Unlimited call minutes" },
      { icon: "ri-database-2-line", text: "CRM integration via FastMCP" },
      { icon: "ri-fingerprint-line", text: "Custom Branded Call ID" },
      { icon: "ri-vip-crown-2-line", text: "Dedicated account manager" },
      { icon: "ri-shield-check-line", text: "99.9% uptime SLA" },
    ],
  },
};

interface StatCardProps {
  icon: string;
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}

function StatCard({ icon, label, value, sub, highlight }: StatCardProps) {
  return (
    <div className={`rounded-2xl border p-5 flex flex-col gap-3 ${highlight ? "bg-amber-50 border-amber-100" : "bg-white border-stone-100"}`}>
      <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${highlight ? "bg-amber-100" : "bg-stone-100"}`}>
        <i className={`${icon} text-base ${highlight ? "text-amber-600" : "text-stone-500"}`} />
      </div>
      <div>
        <p className="text-xs text-stone-400 font-medium mb-0.5">{label}</p>
        <p className={`text-sm font-bold ${highlight ? "text-amber-700" : "text-stone-800"}`}>{value}</p>
        {sub && <p className="text-xs text-stone-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

interface DashboardViewProps {
  data: SubData;
  isEn: boolean;
  onLogout: () => void;
}

export default function DashboardView({ data, isEn, onLogout }: DashboardViewProps) {
  const [billingLoading, setBillingLoading] = useState(false);
  const PLAN_CONFIGS = isEn ? PLAN_CONFIGS_EN : PLAN_CONFIGS_ES;
  const plan = PLAN_CONFIGS[data.plan_id] ?? PLAN_CONFIGS.premium;
  const isPremium = data.plan_id === "premium";
  const isActive = data.status === "active";

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    if (d.getFullYear() > 2090) return isEn ? "No expiry" : "Sin vencimiento";
    return d.toLocaleDateString(isEn ? "en-US" : "es-ES", { day: "2-digit", month: "long", year: "numeric" });
  };

  const memberSince = new Date(data.created_at).toLocaleDateString(isEn ? "en-US" : "es-ES", {
    month: "long",
    year: "numeric",
  });

  const handleBillingPortal = async () => {
    setBillingLoading(true);
    try {
      const res = await fetch(PORTAL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email, returnUrl: window.location.origin + "/cuenta" }),
      });
      const json = await res.json();
      if (json.url) window.location.href = json.url;
    } finally {
      setBillingLoading(false);
    }
  };

  const setupPlanParam = data.plan_id === "premium" ? "premium" : data.plan_id === "profesional" ? "profesional" : "basico";

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Top bar */}
      <div className="bg-white border-b border-stone-100 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="cursor-pointer">
              <img
                src="https://static.readdy.ai/image/464d6b2d88c2c0b329c43db37f7a6b21/5e3d82f418168fbdbc49efd95d2ee821.png"
                alt="Auria"
                className="h-7 w-auto"
              />
            </Link>
            <span className="text-stone-200 text-sm">|</span>
            <span className="text-sm text-stone-500 font-medium">{isEn ? "My Account" : "Mi Cuenta"}</span>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-line" />
            {isEn ? "Sign out" : "Salir"}
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Plan Hero Card */}
        <div className={`rounded-2xl border p-7 mb-6 ${isPremium ? "bg-stone-900 border-stone-800" : "bg-white border-stone-100"}`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${isPremium ? "bg-amber-500/20" : "bg-amber-50"}`}>
                <i className={`${isPremium ? "ri-vip-crown-2-line" : "ri-headphone-line"} text-2xl ${isPremium ? "text-amber-400" : "text-amber-500"}`} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${plan.bgColor} ${plan.color}`}>
                    {plan.nameFull}
                  </span>
                  {isActive && (
                    <span className="flex items-center gap-1 text-xs font-bold text-emerald-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-pulse" />
                      {isEn ? "Active" : "Activo"}
                    </span>
                  )}
                  {data.cancel_at_period_end && (
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-500">
                      {isEn ? "Cancels soon" : "Cancela pronto"}
                    </span>
                  )}
                </div>
                <p
                  className={`text-lg font-bold ${isPremium ? "text-white" : "text-stone-900"}`}
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {isEn ? "Your AI Receptionist" : "Tu Recepcionista IA"}
                </p>
                <p className={`text-xs mt-0.5 ${isPremium ? "text-stone-400" : "text-stone-400"}`}>
                  {data.email} · {isEn ? "Member since" : "Cliente desde"} {memberSince}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`px-3 py-2 rounded-xl text-xs font-medium ${isPremium ? "bg-white/10 text-stone-300" : "bg-stone-50 text-stone-500 border border-stone-100"}`}>
                {data.billing === "annual" ? (isEn ? "Annual billing" : "Pago anual") : (isEn ? "Monthly billing" : "Pago mensual")}
              </div>
            </div>
          </div>
        </div>

        {/* Auria Phone Number — prominent block */}
        {data.assigned_phone ? (
          <div className="mb-6 rounded-2xl overflow-hidden bg-gradient-to-r from-stone-900 to-stone-800 border border-stone-700">
            <div className="px-7 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-emerald-400 text-xl" />
                </div>
                <div>
                  <p className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-0.5">
                    {isEn ? "Your Auria number — active" : "Tu número Auria — activo"}
                  </p>
                  <p className="text-2xl font-black text-white tracking-wider font-mono">{data.assigned_phone}</p>
                </div>
              </div>
              <div className="flex flex-col sm:items-end gap-1.5">
                <p className="text-xs text-stone-400 max-w-xs leading-relaxed">
                  {isEn
                    ? "Forward your current number to this number to activate your AI. GSM codes in your welcome email."
                    : "Desvía tu número actual a este número para activar tu IA. Los códigos GSM están en tu email de bienvenida."}
                </p>
                <Link
                  to={`/bienvenida?plan=${setupPlanParam}&email=${encodeURIComponent(data.email)}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-settings-3-line" />
                  {isEn ? "View setup guide" : "Ver guía de configuración"}
                  <i className="ri-arrow-right-line text-xs" />
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6 rounded-2xl border border-dashed border-stone-200 bg-stone-50 px-7 py-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-stone-200 flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-stone-400 text-lg" />
            </div>
            <div>
              <p className="text-sm font-bold text-stone-600 mb-0.5">
                {isEn ? "Your Auria number is being assigned" : "Tu número Auria está siendo asignado"}
              </p>
              <p className="text-xs text-stone-400 leading-relaxed">
                {isEn
                  ? "You'll receive an email with your number and forwarding instructions within 2 hours."
                  : "Recibirás un email con tu número e instrucciones de desvío en menos de 2 horas."}
              </p>
            </div>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon="ri-headphone-line"
            label={isEn ? "Included minutes" : "Minutos incluidos"}
            value={plan.minutes}
            highlight={isPremium}
          />
          <StatCard
            icon="ri-calendar-check-line"
            label={isEn ? "Renewal date" : "Próxima renovación"}
            value={formatDate(data.current_period_end)}
            sub={data.cancel_at_period_end ? (isEn ? "No renewal" : "Sin renovación") : undefined}
          />
          <StatCard
            icon="ri-repeat-line"
            label={isEn ? "Billing cycle" : "Ciclo de facturación"}
            value={data.billing === "annual" ? (isEn ? "Annual" : "Anual") : (isEn ? "Monthly" : "Mensual")}
            sub={data.billing === "annual" ? (isEn ? "Best value" : "Mejor precio") : undefined}
          />
          <StatCard
            icon="ri-checkbox-circle-line"
            label={isEn ? "Setup status" : "Estado de configuración"}
            value={data.setup_completed ? (isEn ? "Complete" : "Completada") : (isEn ? "Pending" : "Pendiente")}
            sub={data.setup_completed ? undefined : (isEn ? "Tap to continue" : "Toca para continuar")}
          />
        </div>

        {/* Service Status */}
        <div className="mb-6">
          <ServiceStatusWidget isEn={isEn} activeSince={data.created_at} />
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Features + Setup */}
          <div className="lg:col-span-2 space-y-6">
            {/* Setup CTA (if not completed) */}
            {!data.setup_completed && (
              <div className="bg-amber-500 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
                    <i className="ri-settings-3-line text-white text-xl" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white mb-1">
                      {isEn ? "Your setup is not complete yet" : "Tu configuración aún no está completa"}
                    </p>
                    <p className="text-amber-100 text-xs leading-relaxed">
                      {isEn
                        ? "Follow the guide to activate call forwarding, set up Canvas Memory and test your AI."
                        : "Sigue la guía para activar el desvío de llamadas, configurar tu Canvas Memory y probar tu IA."}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/bienvenida?plan=${setupPlanParam}`}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white text-amber-600 text-sm font-bold rounded-xl hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                >
                  <i className="ri-rocket-2-line" />
                  {isEn ? "Complete setup" : "Completar configuración"}
                </Link>
              </div>
            )}

            {/* Features included */}
            <div className="bg-white border border-stone-100 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-sm font-bold text-stone-800">
                  {isEn ? "What's included in your plan" : "Incluido en tu plan"}
                </h2>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${plan.bgColor} ${plan.color}`}>
                  {plan.name}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {plan.features.map((feature) => (
                  <div key={feature.text} className="flex items-center gap-3 py-2 px-3 rounded-xl bg-stone-50 border border-stone-100">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-stone-100 flex-shrink-0">
                      <i className={`${feature.icon} text-amber-500 text-sm`} />
                    </div>
                    <span className="text-xs text-stone-700 font-medium leading-snug">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* How it works */}
            <div className="bg-white border border-stone-100 rounded-2xl p-6">
              <h2 className="text-sm font-bold text-stone-800 mb-5">
                {isEn ? "How your AI receptionist works" : "Cómo funciona tu recepcionista IA"}
              </h2>
              <div className="space-y-4">
                {(isEn ? [
                  { icon: "ri-phone-incoming-line", title: "Client calls your number", body: "Your number gets the call first. If you don't answer in 20 seconds, Auria takes over." },
                  { icon: "ri-brain-line", title: "Auria answers intelligently", body: "The AI responds in the caller's language, using your Canvas Memory to give accurate, real-time information about your business." },
                  { icon: "ri-calendar-check-line", title: "Books appointments automatically", body: "If the caller wants to book, Auria checks your Google Calendar availability and creates the appointment directly." },
                  { icon: "ri-mail-send-line", title: "You receive a summary", body: "After each call you get a complete summary: what was discussed, any appointment booked, and follow-up actions." },
                ] : [
                  { icon: "ri-phone-incoming-line", title: "Tu cliente llama a tu número", body: "Tu teléfono suena primero. Si no contestas en 20 segundos, Auria toma el relevo de forma invisible para el cliente." },
                  { icon: "ri-brain-line", title: "Auria responde con inteligencia", body: "La IA responde en el idioma del cliente, usando tu Canvas Memory para dar información precisa y actualizada de tu negocio." },
                  { icon: "ri-calendar-check-line", title: "Agenda citas automáticamente", body: "Si el cliente quiere reservar, Auria comprueba tu disponibilidad en Google Calendar y crea la cita directamente." },
                  { icon: "ri-mail-send-line", title: "Tú recibes el resumen", body: "Después de cada llamada recibes un resumen completo: qué se habló, si se agendó una cita y qué seguimiento dar." },
                ]).map((step, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-50 flex-shrink-0">
                      <i className={`${step.icon} text-amber-500 text-sm`} />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="text-sm font-bold text-stone-800 mb-0.5">{step.title}</p>
                      <p className="text-xs text-stone-500 leading-relaxed">{step.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Quick Actions + Support */}
          <div className="space-y-5">
            {/* Actions */}
            <div className="bg-white border border-stone-100 rounded-2xl p-5">
              <h2 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">
                {isEn ? "Quick actions" : "Acciones rápidas"}
              </h2>
              <div className="space-y-2">
                <Link
                  to={`/bienvenida?plan=${setupPlanParam}`}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-stone-50 hover:bg-amber-50 border border-stone-100 hover:border-amber-100 transition-all cursor-pointer group"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-stone-100 group-hover:border-amber-200 flex-shrink-0">
                    <i className="ri-settings-3-line text-stone-400 group-hover:text-amber-500 text-sm transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-xs font-bold text-stone-700 whitespace-nowrap">
                      {isEn ? "Setup guide" : "Guía de configuración"}
                    </p>
                    <p className="text-xs text-stone-400 whitespace-nowrap">
                      {data.setup_completed ? (isEn ? "Review steps" : "Revisar pasos") : (isEn ? "Continue setup" : "Continuar configuración")}
                    </p>
                  </div>
                  <i className="ri-arrow-right-s-line text-stone-300 group-hover:text-amber-400 transition-colors" />
                </Link>

                {data.has_stripe && (
                  <button
                    onClick={handleBillingPortal}
                    disabled={billingLoading}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-stone-50 hover:bg-stone-100 border border-stone-100 transition-all cursor-pointer group disabled:opacity-60"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-stone-100 flex-shrink-0">
                      <i className={`${billingLoading ? "ri-loader-4-line animate-spin" : "ri-file-list-3-line"} text-stone-400 text-sm`} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-xs font-bold text-stone-700 whitespace-nowrap">
                        {isEn ? "Manage billing" : "Gestionar facturación"}
                      </p>
                      <p className="text-xs text-stone-400 whitespace-nowrap">
                        {isEn ? "Invoices, change or cancel" : "Facturas, cambio o cancelación"}
                      </p>
                    </div>
                    <i className="ri-external-link-line text-stone-300 group-hover:text-stone-500 transition-colors" />
                  </button>
                )}

                <a
                  href="mailto:soporteauria@gmail.com"
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-stone-50 hover:bg-stone-100 border border-stone-100 transition-all cursor-pointer group"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-stone-100 flex-shrink-0">
                    <i className="ri-mail-line text-stone-400 text-sm" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-xs font-bold text-stone-700 whitespace-nowrap">
                      {isEn ? "Contact support" : "Contactar soporte"}
                    </p>
                    <p className="text-xs text-stone-400 whitespace-nowrap">soporteauria@gmail.com</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-stone-300 group-hover:text-stone-500 transition-colors" />
                </a>
              </div>
            </div>

            {/* Google Calendar Integration */}
            <GoogleCalendarWidget email={data.email} isEn={isEn} />

            {/* Premium: Account manager */}
            {isPremium && (
              <div className="bg-stone-900 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-vip-crown-2-line text-amber-400 text-base" />
                  </div>
                  <p className="text-xs font-bold text-white">
                    {isEn ? "Dedicated account manager" : "Account manager dedicado"}
                  </p>
                </div>
                <p className="text-xs text-stone-400 leading-relaxed mb-4">
                  {isEn
                    ? "You have a team member exclusively assigned to your account for any queries, changes or optimisations."
                    : "Tienes un miembro del equipo Auria asignado exclusivamente a tu cuenta para cualquier consulta, cambio u optimización."}
                </p>
                <a
                  href="mailto:auriareceptionistai@gmail.com"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  auriareceptionistai@gmail.com
                  <i className="ri-arrow-right-line text-xs" />
                </a>
              </div>
            )}

            {/* Plan renewal info */}
            <div className={`rounded-2xl border p-5 ${data.cancel_at_period_end ? "bg-red-50 border-red-100" : "bg-white border-stone-100"}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0 ${data.cancel_at_period_end ? "bg-red-100" : "bg-amber-50"}`}>
                  <i className={`${data.cancel_at_period_end ? "ri-calendar-close-line text-red-400" : "ri-calendar-check-line text-amber-500"} text-sm`} />
                </div>
                <div>
                  <p className={`text-xs font-bold mb-1 ${data.cancel_at_period_end ? "text-red-600" : "text-stone-700"}`}>
                    {data.cancel_at_period_end
                      ? (isEn ? "Subscription ends" : "La suscripción termina")
                      : (isEn ? "Next renewal" : "Próxima renovación")}
                  </p>
                  <p className={`text-sm font-bold ${data.cancel_at_period_end ? "text-red-700" : "text-stone-800"}`}>
                    {formatDate(data.current_period_end)}
                  </p>
                  {!data.cancel_at_period_end && (
                    <p className="text-xs text-stone-400 mt-1">
                      {data.billing === "annual"
                        ? (isEn ? "Annual — best value" : "Anual — mejor precio")
                        : (isEn ? "Monthly billing" : "Facturación mensual")}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Stats summary */}
            <div className="bg-white border border-stone-100 rounded-2xl p-5 space-y-3">
              <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest">
                {isEn ? "Plan summary" : "Resumen del plan"}
              </h3>
              {[
                { label: isEn ? "Plan" : "Plan", value: plan.name },
                { label: isEn ? "Status" : "Estado", value: isActive ? (isEn ? "Active" : "Activo") : (isEn ? "Inactive" : "Inactivo"), green: isActive },
                { label: isEn ? "Minutes" : "Minutos", value: plan.minutes },
                { label: isEn ? "Language" : "Idioma", value: data.preferred_lang ? data.preferred_lang.toUpperCase() : "ES" },
                {
                  label: isEn ? "Setup" : "Configuración",
                  value: data.setup_completed ? (isEn ? "Complete" : "Completa") : (isEn ? "Pending" : "Pendiente"),
                  green: data.setup_completed,
                  warn: !data.setup_completed,
                },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <span className="text-xs text-stone-400">{item.label}</span>
                  <span className={`text-xs font-bold ${item.green ? "text-emerald-500" : item.warn ? "text-amber-500" : "text-stone-700"}`}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom support strip */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 py-5 border-t border-stone-200">
          <p className="text-xs text-stone-400">© 2026 Auria — {isEn ? "All rights reserved" : "Todos los derechos reservados"}</p>
          <div className="flex items-center gap-4">
            <Link to="/privacidad" className="text-xs text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">
              {isEn ? "Privacy" : "Privacidad"}
            </Link>
            <Link to="/terminos" className="text-xs text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">
              {isEn ? "Terms" : "Términos"}
            </Link>
            <a href="mailto:soporteauria@gmail.com" className="text-xs text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">
              {isEn ? "Support" : "Soporte"}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
